import React from 'react';
import developerNyImg from '../assets/developer-ny1.jpg';

const DigitalAgency = () => {
  const benefits = [
    {
      icon: "⏰",
      title: "Save Time",
      description: "Many businesses try to build a website themselves however end up spending so much time trying to figure it all out. Save time by delegating your website to the professionals."
    },
    {
      icon: "💰",
      title: "Save Money",
      description: "You don't have to take out a loan or break the bank to have a professional and modern web presence. Our prices are a fraction of what other website design firms charge."
    },
    {
      icon: "🎨",
      title: "Look Professional",
      description: "People judge books by their cover. Most businesses don't realize the importance of having a professionally designed website. You could be losing potential clients with an outdated or DIY website."
    },
    {
      icon: "📈",
      title: "Grow Your Business",
      description: "Our websites don't just look great, they're designed to grow your business. Start attracting more customers online with a beautiful, search-optimized website for your business."
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Tell us about your business",
      description: "Start by telling us a little about your business, and we'll begin designing your website in as little as 24 hours.",
      details: [
        "Your business name",
        "A brief description of your company",
        "Core products and services"
      ]
    },
    {
      step: "2",
      title: "We design your website for FREE",
      description: "Our designers get to work putting together a beautiful website design based on your input and any information you provided.",
      details: [
        "Preview your website for free",
        "Make changes before payment",
        "100% satisfaction guaranteed"
      ]
    },
    {
      step: "3",
      title: "Review your site and go live",
      description: "Once you approve the design and submit payment, we will make any changes, add content, complete your site, and publish your site for the world to see!",
      details: [
        "Ongoing monthly updates",
        "Hosting and site security",
        "Professional support"
      ]
    }
  ];

  const features = [
    "Professional, Modern Design",
    "Mobile-Friendly",
    "Search Engine Optimized",
    "Real-time Traffic Stats and Analytics",
    "Hosting and Updates Included",
    "Friendly and Reliable Support",
    "Go Live in as Little as 1 Week!"
  ];

  const testimonials = [
    {
      quote: "I was hesitant to invest in a website for my small business, but I'm so glad I did! The team was great to work with – they were knowledgeable, responsive, and patient with all of my questions.",
      author: "Chris S.",
      business: "Small Business Owner"
    },
    {
      quote: "As a small business owner, I was looking for a web design firm that could deliver high-quality work at an affordable price. That's exactly what I found with AdroitOneZ.",
      author: "Heather F.",
      business: "Local Entrepreneur"
    },
    {
      quote: "I can't thank the team enough for the amazing job they did on my website. They listened to my needs and created a site that perfectly reflects my brand and message.",
      author: "Samantha H.",
      business: "Business Owner"
    }
  ];

  const faqs = [
    {
      question: "Why should I use AdroitOneZ to create my website?",
      answer: "Maybe you created your own website and the design didn't turn out quite how you anticipated, or you have an outdated website that needs to be updated but you don't want to spend a lot. For us, quality and good service do not have to be expensive."
    },
    {
      question: "Will you really design my site for free?",
      answer: "Yes. Each project starts with a free design demo to show you what we're capable. This ensures you are 100% happy with our work before you pay us a dime."
    },
    {
      question: "What's included in the monthly service fee?",
      answer: "The monthly service fee includes hosting, SSL certificate, ongoing maintenance and support for our clients to ensure that their websites remain up-to-date and functioning properly."
    },
    {
      question: "Will my new website look good on smartphones?",
      answer: "Yes. All of the websites we design are built to be responsive, meaning that they will automatically adjust to fit the screen size of any device."
    }
  ];

  const portfolioExamples = [
    "Eyelash eyebrow threading salon",
    "Painter website design",
    "Podiatrist website",
    "Real estate photography",
    "Ice cream shop website",
    "Fence contractor website",
    "Car audio security",
    "Title company website",
    "Truck accessories store",
    "Snow removal website",
    "Dryer vent cleaning service",
    "Mobile phone repair website"
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-cyan-500 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              New York's Most-Trusted
            </h1>
            <p className="text-2xl md:text-3xl text-cyan-100 mb-6 font-semibold">
              Website Design Development & Digital Agency
            </p>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              We offer high-quality, affordable websites for small businesses that always start with a FREE demo.
            </p>
            <button className="bg-white text-cyan-600 hover:bg-cyan-50 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl shadow-lg text-lg">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group hover:transform hover:-translate-y-2 transition-all duration-300">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Welcome to AdroitOneZ
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                AdroitOneZ is a Top-Rated Website Designer in New York, NY. We specialize in creating affordable websites for small businesses in New York and beyond.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At AdroitOneZ, we understand that as a small business owner, you have a lot on your plate and a limited budget to work with. That's why we offer cost-effective solutions to help you get a professional, functional website that meets your needs and helps you grow your business.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                If you're interested in seeing what we can do for your business, contact us today for a FREE no-obligation website demo.
              </p>
              <button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl shadow-lg text-lg">
                Get Started Now
              </button>
            </div>
            
            {/* Welcome Image */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2 h-96 flex items-center justify-center border border-white/20 overflow-hidden">
                <img 
                  src={developerNyImg} 
                  alt="New York based web developers working" 
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-400 rounded-full opacity-20 blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-300 rounded-full opacity-20 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              It's as Easy as 1, 2, 3...
            </h2>
            <p className="text-xl text-gray-600">
              You are just a few small steps away from growing your business online.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{step.description}</p>
                <ul className="text-left text-gray-600 space-y-2">
                  {step.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-cyan-500 mr-2">•</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              All the features you need - for one low price
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <p className="text-gray-600 mb-4">... and much more!</p>
              <button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg">
                Get Started Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Examples */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Website Examples
            </h2>
            <p className="text-xl text-gray-600">
              Check out some samples of our high-quality work below to see what we're capable of.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {portfolioExamples.map((example, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-full h-32 bg-gradient-to-br from-blue-400 to-cyan-300 rounded mb-3 flex items-center justify-center text-white text-2xl">
                  🌐
                </div>
                <div className="font-semibold text-gray-900 text-sm">{example}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Testimonials
            </h2>
            <p className="text-xl text-gray-600">
              Here's what our customers have to say
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="text-yellow-400 text-xl mb-4">★★★★★</div>
                <p className="text-gray-700 italic mb-6">"{testimonial.quote}"</p>
                <div className="border-t pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.author}</div>
                  <div className="text-gray-600 text-sm">{testimonial.business}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-cyan-500 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready for Your Free Demo?
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            See your vision come to life with our no-obligation free demo and consultation.
          </p>
          <button className="bg-white text-cyan-600 hover:bg-cyan-50 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl shadow-lg text-lg">
            Get Your Free Demo
          </button>
        </div>
      </section>
    </div>
  );
};

export default DigitalAgency;